<script>
import { abilitiesPlugin } from '@casl/vue';
import ability from '@/casl/ability';
import TenantContextService from '@/service/TenantContextService';
import { loadRuntimeContext } from '@/tenancy/runtimeContext';

export default {
  name: 'AppLayout',

  data() {
    return {
      directives: {
        can: abilitiesPlugin.directive,
      },
      drawer: false,
      showTipoSubMenu: false,
      tenantOptions: [],
      activeTenantSlug: localStorage.getItem('active_tenant_slug') || '',
      switchingTenant: false,
      platformName: '',
      runtimeScope: '',
      tenantName: '',
      items: [
        { title: 'Empresas', icon: 'mdi-office-building-cog-outline', link: '/admin/empresas', platformOnly: true },
        { title: 'Mi perfil', icon: 'mdi-account-circle-outline', link: '/admin/perfil' },
        { title: 'Configuración sitio', icon: 'mdi-tune-variant', link: '/admin/configuracion-sitio' },
        { title: 'Empresa y módulos', icon: 'mdi-domain', link: '/admin/empresa', tenantManagementOnly: true, centralOnly: true },
        { title: 'Canales de venta', icon: 'mdi-access-point', link: '/admin/canales' },
        { title: 'Ventas y pedidos', icon: 'mdi-receipt-text-check-outline', link: '/admin/ventas' },
        { title: 'Ventas multicanal', icon: 'mdi-cart-check', link: '/admin/ventas-canales' },
        { title: 'Publicar catálogo', icon: 'mdi-publish', link: '/admin/publicaciones' },
        { title: 'Blog y noticias', icon: 'mdi-newspaper-variant-outline', link: '/admin/contenido' },
      ],
      tipoItems: [
        { title: 'Producto', icon: 'mdi-package-variant-closed', link: '/admin/producto' },
        { title: 'Categoria', icon: 'mdi-shape-outline', link: '/admin/categoria' },
      ],
    };
  },

  computed: {
    can() {
      return this.$can;
    },

    currentUser() {
      try {
        return JSON.parse(localStorage.getItem('userData')) || {};
      } catch (_) {
        return {};
      }
    },

    isPlatformAdmin() {
      return Boolean(this.currentUser.is_platform_admin);
    },

    isPlatformContext() {
      return this.runtimeScope === 'platform';
    },

    brandName() {
      return this.isPlatformContext ? this.platformName : this.tenantName;
    },

    brandInitial() {
      return this.brandName.trim().charAt(0).toUpperCase() || 'A';
    },

    visibleItems() {
      return this.items.filter((item) => {
        if (item.platformOnly && (!this.isPlatformAdmin || !this.isPlatformContext)) return false;
        if (item.centralOnly && !this.isPlatformContext) return false;
        if (this.isPlatformContext && this.isPlatformAdmin && !this.activeTenantSlug && !item.platformOnly) return false;
        if (item.tenantManagementOnly && !this.currentUser.can_manage_tenant) return false;
        return true;
      });
    },
  },

  created() {
    loadRuntimeContext().then((context) => {
      this.runtimeScope = context.scope || '';
      this.platformName = context.platform?.name || '';
      this.tenantName = context.tenant?.name || '';
      if (context.scope === 'tenant' && context.tenant?.slug) {
        this.activeTenantSlug = context.tenant.slug;
        localStorage.setItem('active_tenant_slug', context.tenant.slug);
        return;
      }
      this.loadTenantOptions();
    }).catch(() => {});
  },

  watch: {
    $route() {
      if (this.$vuetify.breakpoint.smAndDown) this.drawer = false;
    },
  },

  methods: {
    async loadTenantOptions() {
      try {
        const { data } = await TenantContextService.options();
        this.tenantOptions = data.tenants || [];
        const selected = data.active_tenant?.slug || this.activeTenantSlug;
        if (selected) {
          this.activeTenantSlug = selected;
          localStorage.setItem('active_tenant_slug', selected);
        }
      } catch (_) {
        this.tenantOptions = [];
      }
    },

    async switchTenant(slug) {
      if (!slug || this.switchingTenant) return;

      this.switchingTenant = true;
      try {
        const { data } = await TenantContextService.switchTo(slug);
        localStorage.setItem('active_tenant_slug', data.tenant.slug);
        localStorage.setItem('userData', JSON.stringify(data.user));
        localStorage.setItem('permisos', JSON.stringify(data.user.permisos || []));
        ability.update(data.user.permisos || []);
        this.activeTenantSlug = data.tenant.slug;

        if (this.$route.path === '/admin/empresas') {
          this.$router.push('/admin/empresa');
        } else {
          window.location.reload();
        }
      } catch (_) {
        this.$toast?.error?.('No se pudo cambiar la empresa activa.');
      } finally {
        this.switchingTenant = false;
      }
    },
    toggleTipoSubMenu() {
      this.showTipoSubMenu = !this.showTipoSubMenu;
    },

    toggleDrawer() {
      this.drawer = !this.drawer;
    },

    handleItemClick(item) {
      if (item.action && typeof this[item.action] === 'function') {
        this[item.action]();
        return;
      }

      if (item.link !== this.$route.fullPath) {
        this.$router.push(item.link);
      } else if (this.$vuetify.breakpoint.smAndDown) {
        this.drawer = false;
      }
    },

    async onSalir() {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        localStorage.removeItem('permisos');
        localStorage.removeItem('active_tenant_slug');
        this.$router.push({ name: 'Login' });
      } catch (error) {
        alert('Ocurrió un problema al salir');
      }
    },
  },
};
</script>

<template>
  <v-app class="admin-shell">
    <v-app-bar app flat dark color="#111111" height="58" class="admin-topbar">
      <v-app-bar-nav-icon aria-label="Abrir menú" @click.stop="toggleDrawer" />

      <div class="admin-brand" @click="$router.push('/admin/perfil')">
        <div class="admin-brand-mark">{{ brandInitial }}</div>
        <div class="admin-brand-copy hidden-xs-only">
          <strong>{{ brandName }}</strong>
          <span>Administración</span>
        </div>
      </div>

      <v-spacer />

      <v-select
        v-if="isPlatformContext && tenantOptions.length"
        v-model="activeTenantSlug"
        :items="tenantOptions"
        item-text="name"
        item-value="slug"
        :loading="switchingTenant"
        :disabled="switchingTenant"
        aria-label="Empresa activa"
        title="Empresa activa"
        dense
        outlined
        hide-details
        dark
        class="tenant-selector"
        @change="switchTenant"
      />

      <v-btn icon aria-label="Cerrar sesión" title="Cerrar sesión" @click="onSalir">
        <v-icon>mdi-logout</v-icon>
      </v-btn>
    </v-app-bar>

    <v-navigation-drawer
      v-model="drawer"
      app
      dark
      color="#181818"
      width="272"
      :temporary="$vuetify.breakpoint.smAndDown"
      class="admin-drawer"
    >
      <div class="drawer-heading">
        <div class="drawer-title">Panel de administración</div>
        <div class="drawer-subtitle">{{ isPlatformContext ? 'Gestión central' : 'Administración de la empresa' }}</div>
      </div>

      <v-divider class="drawer-divider" />

      <v-list nav dense class="px-3 py-3">
        <v-list-item
          v-for="item in visibleItems"
          :key="item.title"
          :class="{ 'admin-menu-active': item.link === $route.path }"
          class="admin-menu-item"
          @click="handleItemClick(item)"
        >
          <v-list-item-icon>
            <v-icon size="20">{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-group
          v-model="showTipoSubMenu"
          prepend-icon="mdi-table-large"
          no-action
          class="admin-menu-group"
        >
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title>Tablas</v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item
            v-for="subitem in tipoItems"
            :key="subitem.title"
            :class="{ 'admin-menu-active': subitem.link === $route.path }"
            class="admin-menu-item admin-submenu-item"
            @click="handleItemClick(subitem)"
          >
            <v-list-item-icon>
              <v-icon size="18">{{ subitem.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ subitem.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-group>

        <template v-if="can('manage', 'all')">
          <v-divider class="my-3 drawer-divider" />

          <v-list-item link to="/admin/carga" class="admin-menu-item">
            <v-list-item-icon><v-icon size="20">mdi-file-excel-outline</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title>Carga Excel</v-list-item-title></v-list-item-content>
          </v-list-item>

          <v-list-item link to="/admin/permiso" class="admin-menu-item">
            <v-list-item-icon><v-icon size="20">mdi-shield-key-outline</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title>Permisos</v-list-item-title></v-list-item-content>
          </v-list-item>

          <v-list-item link to="/admin/role" class="admin-menu-item">
            <v-list-item-icon><v-icon size="20">mdi-account-key-outline</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title>Roles</v-list-item-title></v-list-item-content>
          </v-list-item>

          <v-list-item link to="/admin/usuario" class="admin-menu-item">
            <v-list-item-icon><v-icon size="20">mdi-account-multiple-outline</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title>Usuarios</v-list-item-title></v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <v-main class="admin-main">
      <div class="admin-content">
        <router-view />
      </div>
    </v-main>
  </v-app>
</template>

<style scoped>
.admin-shell {
  background: #f4f4f4 !important;
  color: #191919;
  text-align: left;
}

.admin-topbar {
  border-bottom: 1px solid #2b2b2b !important;
}

.tenant-selector {
  flex: 0 1 250px;
  max-width: 250px;
  margin-right: 8px;
}

.tenant-selector ::v-deep .v-input__slot {
  min-height: 38px !important;
}

.admin-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.admin-brand-mark {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  color: #111111;
  font-weight: 900;
  font-size: 1rem;
}

.admin-brand-copy {
  display: flex;
  flex-direction: column;
  line-height: 1.05;
}

.admin-brand-copy strong {
  color: #ffffff;
  font-size: .92rem;
  letter-spacing: .02em;
}

.admin-brand-copy span {
  color: #a7a7a7;
  font-size: .68rem;
  margin-top: 3px;
}

.admin-drawer {
  border-right: 1px solid #2a2a2a !important;
}

.drawer-heading {
  padding: 24px 20px 18px;
}

.drawer-title {
  color: #ffffff;
  font-size: .93rem;
  font-weight: 700;
}

.drawer-subtitle {
  color: #909090;
  font-size: .75rem;
  margin-top: 4px;
}

.drawer-divider {
  border-color: #303030 !important;
}

.admin-menu-item {
  border-radius: 9px !important;
  min-height: 42px !important;
  margin: 3px 0;
  color: #d5d5d5 !important;
}

.admin-menu-item:hover,
.admin-menu-active {
  background: #2a2a2a !important;
  color: #ffffff !important;
}

.admin-menu-item .v-list-item__icon {
  margin-right: 14px !important;
}

.admin-menu-item .v-list-item__title,
.admin-menu-group ::v-deep .v-list-item__title {
  font-size: .82rem !important;
  font-weight: 500;
}

.admin-submenu-item {
  padding-left: 20px !important;
}

.admin-main {
  min-width: 0;
  background: #f4f4f4;
}

.admin-content {
  width: 100%;
  max-width: 1480px;
  min-width: 0;
  margin: 0 auto;
  padding: 22px;
  box-sizing: border-box;
}

@media (max-width: 600px) {
  .tenant-selector {
    max-width: 148px;
  }
  .admin-content {
    padding: 10px 8px 24px;
    overflow-x: hidden;
  }

  .admin-brand-mark {
    width: 30px;
    height: 30px;
    border-radius: 8px;
  }
}
</style>
